import { Button } from "devextreme-react/button";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { companyNameById, SharedEmployee2 } from "../types";

type Actions = {
  onShare: (row: SharedEmployee2) => void;
  onRemove: (row: SharedEmployee2) => void;
  onEditRange: (row: SharedEmployee2) => void;
  onWithdraw: (row: SharedEmployee2) => void;
};

type Props = {
  employees: SharedEmployee2[];
  isMobile: boolean;
  search: string;
  canAdminWithdraw: (row: SharedEmployee2) => boolean;
} & Actions;

const keyOf = (row: { companyId: number; employeeId: number }) =>
  `${row.companyId}-${row.employeeId}`;

export const EmployeesView2 = memo(function EmployeesView2({
  employees,
  isMobile,
  search,
  canAdminWithdraw,
  onShare,
  onRemove,
  onEditRange,
  onWithdraw,
}: Props) {
  const { t } = useTranslation();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      [e.surname, e.name, e.position, companyNameById(e.companyId)]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [employees, search]);

  const getStatus = (row: SharedEmployee2) => {
    if ((row.temporaryManagerId ?? 0) !== 0)
      return t("transferOfEmployees2.status.rented");
    if (row.managerId !== 0) return t("transferOfEmployees2.status.onExchange");
    return t("transferOfEmployees2.status.available");
  };

  if (isMobile) {
    if (filtered.length === 0) {
      return (
        <div className="transfer2-empty" role="alert">
          {t("transferOfEmployees2.emptyEmployees")}
        </div>
      );
    }

    return (
      <div className="transfer2-cards">
        {filtered.map((row) => {
          const canShare =
            row.managerId === 0 && (row.temporaryManagerId ?? 0) === 0;
          const canRemove =
            row.managerId !== 0 &&
            (row.temporaryManagerId ?? 0) === 0 &&
            row.canManage;
          const canEdit =
            row.managerId !== 0 &&
            (row.temporaryManagerId ?? 0) === 0 &&
            row.canManage;
          const canWithdraw =
            (row.temporaryManagerId ?? 0) !== 0 &&
            (row.canManage || canAdminWithdraw(row));

          return (
            <div key={keyOf(row)} className="transfer2-card">
              <div className="transfer2-card-header">
                <div className="transfer2-card-name">
                  {row.surname} {row.name}
                </div>
                {row.position && (
                  <div className="transfer2-card-sub">{row.position}</div>
                )}
                <span className="transfer2-card-status">{getStatus(row)}</span>
              </div>
              <div className="transfer2-card-body">
                <div className="transfer2-card-row">
                  <span className="transfer2-card-label">{t("company")}:</span>
                  <span>{companyNameById(row.companyId)}</span>
                </div>
                {row.temporaryManagerFullName && (
                  <div className="transfer2-card-row">
                    <span className="transfer2-card-label">
                      {t("transferOfEmployees2.borrowedBy")}:
                    </span>
                    <span>{row.temporaryManagerFullName}</span>
                  </div>
                )}
              </div>
              <div className="transfer2-card-actions">
                {canShare && (
                  <Button
                    icon="add"
                    type="default"
                    stylingMode="contained"
                    text={t("transferOfEmployees2.shareAction")}
                    width="100%"
                    onClick={() => onShare(row)}
                  />
                )}
                {canEdit && (
                  <Button
                    icon="edit"
                    stylingMode="outlined"
                    text={t("transferOfEmployees2.editRangeAction")}
                    width="100%"
                    onClick={() => onEditRange(row)}
                  />
                )}
                {canRemove && (
                  <Button
                    icon="trash"
                    type="danger"
                    stylingMode="outlined"
                    text={t("transferOfEmployees2.removeAction")}
                    width="100%"
                    onClick={() => onRemove(row)}
                  />
                )}
                {canWithdraw && (
                  <Button
                    icon="undo"
                    stylingMode="outlined"
                    text={t("transferOfEmployees2.withdrawAction")}
                    width="100%"
                    onClick={() => onWithdraw(row)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DataGrid
      dataSource={filtered}
      keyExpr={["companyId", "employeeId"]}
      showBorders
      columnAutoWidth
      allowColumnResizing
      allowColumnReordering
      noDataText={t("transferOfEmployees2.emptyEmployees")}
      searchPanel={{
        visible: true,
        placeholder: t("transferOfEmployees.Search..."),
      }}
      paging={{ pageSize: 10 }}
      pager={{ visible: true, showInfo: true, showNavigationButtons: true }}
    >
      <Toolbar>
        <DataGridItem
          name="searchPanel"
          location="before"
          locateInMenu="auto"
          cssClass="datagrid-search-panel-without-margin-left"
        />
      </Toolbar>
      <FilterRow visible />
      <Column
        dataField="surname"
        dataType="string"
        caption={t("transferOfEmployees.lastName")}
        minWidth={180}
      />
      <Column
        dataField="name"
        dataType="string"
        caption={t("transferOfEmployees.firstName")}
        minWidth={180}
      />
      <Column
        dataField="companyId"
        dataType="string"
        caption={t("company")}
        customizeText={({ value }) => companyNameById(value as number)}
        minWidth={140}
      />
      <Column
        dataField="position"
        dataType="string"
        caption={t("transferOfEmployees.position")}
        minWidth={180}
      />
      <Column
        caption={t("transferOfEmployees2.statusHeader")}
        calculateCellValue={getStatus}
        minWidth={140}
      />
      <Column
        dataField="temporaryManagerFullName"
        dataType="string"
        caption={t("transferOfEmployees2.borrowedBy")}
        minWidth={200}
      />
      <Column
        type="buttons"
        caption={t("transferOfEmployees2.actions")}
        width={180}
        buttons={[
          {
            icon: "add",
            hint: t("transferOfEmployees2.shareAction"),
            onClick: (e) => e.row?.data && onShare(e.row.data),
            visible: (e) => {
              const r = e.row?.data as SharedEmployee2 | undefined;
              return (
                !!r && r.managerId === 0 && (r.temporaryManagerId ?? 0) === 0
              );
            },
          },
          {
            icon: "edit",
            hint: t("transferOfEmployees2.editRangeAction"),
            onClick: (e) => e.row?.data && onEditRange(e.row.data),
            visible: (e) => {
              const r = e.row?.data as SharedEmployee2 | undefined;
              return (
                !!r &&
                r.managerId !== 0 &&
                (r.temporaryManagerId ?? 0) === 0 &&
                r.canManage
              );
            },
          },
          {
            icon: "trash",
            hint: t("transferOfEmployees2.removeAction"),
            onClick: (e) => e.row?.data && onRemove(e.row.data),
            visible: (e) => {
              const r = e.row?.data as SharedEmployee2 | undefined;
              return (
                !!r &&
                r.managerId !== 0 &&
                (r.temporaryManagerId ?? 0) === 0 &&
                r.canManage
              );
            },
          },
          {
            icon: "undo",
            hint: t("transferOfEmployees2.withdrawAction"),
            onClick: (e) => e.row?.data && onWithdraw(e.row.data),
            visible: (e) => {
              const r = e.row?.data as SharedEmployee2 | undefined;
              return (
                !!r &&
                (r.temporaryManagerId ?? 0) !== 0 &&
                (r.canManage || canAdminWithdraw(r))
              );
            },
          },
        ]}
      />
    </DataGrid>
  );
});
